import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { UserRole } from '../../types';
import ThemeToggle from '../../components/ThemeToggle';
import { useTheme } from '../../context/ThemeContext';


const SIGNALS = [
  { num: '01', name: 'Vehicle Zone Coverage', desc: 'Did the rep show every part of the car? Front, rear, interior, engine, wheels \u2014 scored on completeness.', tag: 'visual' },
  { num: '02', name: 'Salesperson On Camera', desc: 'Is there a face building trust \u2014 or just a voiceover? Human connection is a measurable conversion driver.', tag: 'visual' },
  { num: '03', name: 'Lighting Quality', desc: 'Does the vehicle look its best? Luminance, shadow, and glare scored frame by frame.', tag: 'visual' },
  { num: '04', name: 'Camera Stability', desc: 'Shaky handheld or deliberate presentation? Stability is a proxy for professionalism.', tag: 'visual' },
  { num: '05', name: 'Video Duration', desc: 'Is the video in the optimal engagement window? Too short is lazy. Too long loses the buyer.', tag: 'visual' },
  { num: '06', name: 'Speech Clarity & Pace', desc: 'Can the buyer hear and understand? Confidence, clarity, and delivery speed \u2014 all measured.', tag: 'audio' },
  { num: '07', name: 'Energy & Enthusiasm', desc: 'Powered by audEERING emotional expression analysis. Flat delivery is invisible to the eye — not to VQS.', tag: 'ai' },
  { num: '08', name: 'Buyer Name Mentioned', desc: "Is this video personal or generic? A buyer\u2019s name is the simplest personalization signal \u2014 and one of the most powerful.", tag: 'audio' },
  { num: '09', name: 'Feature Callout Count', desc: 'Does the rep know the product? Feature depth is a signal for conviction \u2014 and conviction closes deals.', tag: 'audio' },
  { num: '10', name: 'Call to Action Present', desc: 'Did the video ask the buyer to do something? A video without a CTA is a monologue. VQS makes it visible.', tag: 'audio' },
];

const SCORE_BARS = [
  { label: 'Vehicle Coverage', pct: 95, cls: 'high' },
  { label: 'Energy & Enthusiasm', pct: 91, cls: 'high' },
  { label: 'Buyer Name Used', pct: 100, cls: 'high' },
  { label: 'Call to Action', pct: 100, cls: 'high' },
  { label: 'Speech Clarity', pct: 78, cls: 'mid' },
  { label: 'Lighting Quality', pct: 72, cls: 'mid' },
];

const COMPLIANCE_FLAGS = [
  { name: 'Profanity', method: 'Transcript keyword detection', severity: 'block', icon: '\u2715' },
  { name: 'Verbal Pricing or Financing Claim', method: 'NLP pattern matching', severity: 'block', icon: '\u2715' },
  { name: 'Competitor Vehicle Mention', method: 'Configurable brand list', severity: 'block', icon: '\u2715' },
  { name: 'Customer PII on Camera', method: 'Regex + NLP detection', severity: 'block', icon: '\u2715' },
  { name: 'Discriminatory Language', method: 'Curated phrase list', severity: 'block', icon: '\u2715' },
  { name: 'Negative Dealership Sentiment', method: 'Sentiment analysis', severity: 'warn', icon: '!' },
  { name: 'Excessive Filler Words', method: 'Transcript density analysis', severity: 'warn', icon: '!' },
];

const GRADES = [
  { letter: 'A', label: 'Elite', range: '85\u2013100', desc: 'Exceptional delivery. Feature as an internal coaching benchmark.', cls: 'a' },
  { letter: 'B', label: 'Proficient', range: '70\u201384', desc: 'Solid video. Coach on the lowest sub-score.', cls: 'b' },
  { letter: 'C', label: 'Adequate', range: '55\u201369', desc: 'Functional but generic. Flag for coaching intervention.', cls: 'c' },
  { letter: 'D', label: 'Weak', range: '40\u201354', desc: 'Multiple gaps. Manager review required.', cls: 'd' },
  { letter: 'F', label: 'Failing', range: '0\u201339', desc: 'Do not send without revision. Immediate escalation.', cls: 'f' },
];

const PARTNERS = ['CDK Global', 'Reynolds & Reynolds', 'Tekion', 'Solera', 'VinSolutions', 'Keyloop', 'Salesforce', 'Vonage'];

const LandingPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Background visibility
      setIsScrolled(currentScrollY > 50);

      // Hide on scroll down, show on scroll up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userRole = useAuthStore((s) => s.user?.role);
  const { theme } = useTheme();
  const dashboardPath = userRole === UserRole.SUPER_ADMIN ? '/tenants' : '/dashboard';
  const logoSrc = theme === 'light' ? '/truvideo-logo-dark.png' : '/truvideo-logo.png';
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Intersection observer for scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );

    const els = revealRefs.current.filter(Boolean) as HTMLDivElement[];
    els.forEach((el) => observer.observe(el));

    return () => {
      els.forEach((el) => observer.unobserve(el));
    };
  }, []);

  let revealIdx = 0;
  const revealRef = (el: HTMLDivElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };
  const nextReveal = () => {
    revealIdx++;
    return revealRef;
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white font-sans selection:bg-primary/30 overflow-x-hidden transition-colors duration-500">
      {/* BACKGROUND DECORATIONS */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden select-none z-[1]">
        <div className="absolute top-0 right-0 h-full hidden xl:flex items-center pr-16 opacity-50">
          <h1 className="text-[min(12vw,14vh)] font-black text-slate-200 dark:text-white/3 uppercase tracking-tighter transform rotate-[-90deg] origin-right leading-none whitespace-nowrap translate-y-[80%] select-none">
            SALES
          </h1>
        </div>
      </div>

      {/* Decorative Glow (Glassy Effect) */}
      <div
        className="absolute pointer-events-none opacity-60"
        style={{
          top: '-14%',
          left: '-28%',
          width: '80%',
          height: '26%',
          background: 'radial-gradient(circle at top left, #0099d681, #0000 60%)',
          filter: 'blur(60px)',
        }}
      />

      {/* NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-20 flex items-center transition-all duration-500 transform ${isVisible ? 'translate-y-0' : '-translate-y-full'} ${isMobileMenuOpen || isScrolled ? 'bg-white dark:bg-slate-950/90 backdrop-blur-xl border-b border-slate-200/50 dark:border-white/5 shadow-sm' : 'bg-transparent shadow-none'}`}>
        <div className="max-w-7xl mx-auto w-full px-6 flex items-center justify-between">
          <img src={logoSrc} alt="TruVideo" className="h-8 lg:h-9 transition-opacity hover:opacity-80 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
          
          <div className="flex items-center gap-4 lg:gap-8">
            <ThemeToggle />

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-4">
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate(dashboardPath)} 
                  className="h-10 px-5 rounded-xl border border-slate-200 dark:border-white/10 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-all bg-transparent"
                >
                  Go to Dashboard
                </button>
              ) : (
                <button 
                  onClick={() => navigate('/login')} 
                  className="h-10 px-5 rounded-xl border border-slate-200 dark:border-white/10 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-all bg-transparent"
                >
                  Login
                </button>
              )}
              <button 
                onClick={() => scrollTo('contact')} 
                className="h-10 px-6 rounded-xl bg-primary text-white text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary/90 hover:shadow-primary/30 transition-all active:scale-[0.98]"
              >
                Request Demo
              </button>
            </div>

            {/* Mobile Toggle */}
            <button
              className="lg:hidden p-2 z-[110] relative text-slate-900 dark:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between relative">
                <span className={`block w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2.5' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* MOBILE MENU OVERLAY */}
        <div className={`fixed inset-0 z-[100] bg-white dark:bg-slate-950 flex flex-col transition-all duration-500 ease-in-out ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          <div className="flex flex-col items-center justify-center h-full gap-8 px-10">
            {isAuthenticated ? (
              <button onClick={() => { navigate(dashboardPath); setIsMobileMenuOpen(false); }} className="text-3xl font-bold text-slate-900 dark:text-white hover:text-primary transition-colors">
                Dashboard
              </button>
            ) : (
              <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }} className="text-3xl font-bold text-slate-900 dark:text-white hover:text-primary transition-colors">
                Login
              </button>
            )}
            <button onClick={() => { scrollTo('signals'); setIsMobileMenuOpen(false); }} className="text-3xl font-bold text-slate-900 dark:text-white hover:text-primary transition-colors">
              Signals
            </button>
            <button 
              onClick={() => { scrollTo('contact'); setIsMobileMenuOpen(false); }} 
              className="w-full h-16 rounded-2xl bg-primary text-white text-xl font-bold mt-4 shadow-xl shadow-primary/30 active:scale-[0.98]"
            >
              Request Demo
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center pt-32 pb-0 lg:pt-40 lg:pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-12 lg:gap-24 items-center w-full pb-10 relative z-10">

          {/* HERO LEFT */}
          <div className="relative z-10 flex flex-col justify-center py-20">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-12 bg-slate-200 dark:bg-white/10 transition-all" />
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                New Product Launch &mdash; March 2026
              </div>
            </div>

            <h1 className="font-sans text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight text-slate-900 dark:text-white mb-6">
              VQS<br /><span className="text-primary italic">SALES</span>
            </h1>

            <p className="font-sans text-lg lg:text-xl font-medium text-slate-500 dark:text-slate-400 leading-relaxed mb-10 max-w-lg">
              The first AI scoring system built for automotive sales video.
            </p>

            <p className="text-base text-slate-400 dark:text-slate-500 mb-12 max-w-md leading-relaxed hidden lg:block">
              Every video your sales team sends is either building trust or losing the deal. For the first time, you'll know which &mdash; and why.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollTo('contact')}
                className="h-12 px-8 rounded-xl bg-primary text-white font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-[0.98]"
              >
                Request a Demo
              </button>
              <button
                onClick={() => scrollTo('signals')}
                className="h-12 px-8 rounded-xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 font-semibold hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-all active:scale-[0.98]"
              >
                See How It Works
              </button>
            </div>
          </div>

          {/* HERO RIGHT (SCORE CARD) */}
          <div className="relative z-10 py-10 lg:py-20 flex justify-center lg:justify-end">
            <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-white/10 backdrop-blur-2xl p-8 lg:p-10 rounded-[2.5rem] shadow-2xl shadow-slate-200/50 dark:shadow-none w-full max-w-md transform hover:scale-[1.02] transition-all duration-500 animate-float">
              <div className="flex justify-between items-start mb-8 pb-6 border-b border-slate-200">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 dark:text-slate-500 mb-1">VQS Sales Score</div>
                  <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">Prestige Motors &mdash; Mike Reyes</div>
                </div>
                <div className="text-right">
                  <div className="text-7xl lg:text-8xl font-black text-green-600 dark:text-green-500 leading-none tracking-tighter">A</div>
                  <div className="text-[10px] font-bold text-slate-400 dark:text-slate-600 mt-2">88 / 100</div>
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                {SCORE_BARS.map((bar) => (
                  <div key={bar.label} className="flex items-center gap-4">
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 w-32 shrink-0 uppercase tracking-widest">{bar.label}</span>
                    <div className="flex-1 h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-1000 ${bar.cls === 'high' ? 'bg-green-500' : 'bg-blue-500'}`} 
                        style={{ width: `${bar.pct}%` }} 
                      />
                    </div>
                    <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 w-8 text-right font-mono">{bar.pct}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center gap-3 bg-green-50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/20 p-4 rounded-2xl shadow-sm">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                <div className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-widest">No compliance flags detected</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-20 lg:py-24 px-6 bg-slate-900 text-white transition-colors relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div ref={nextReveal()} className="landing-reveal transition-all duration-1000">
            <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-primary mb-6 block">The Problem</span>
            <h2 className="font-sans text-4xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-white mb-8">
              Video Without<br />Intelligence Is Just<br />Content.
            </h2>
            <p className="text-lg lg:text-xl text-slate-400 leading-relaxed max-w-2xl font-medium">
              Your sales team sends thousands of videos a year. You have no idea if any of them are working &mdash; or why.
            </p>
          </div>
          
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            <div ref={nextReveal()} className="landing-reveal group relative flex flex-col items-start gap-6 p-8 bg-white/5 border border-white/10 rounded-[2rem] transition-all duration-500 shadow-sm shadow-primary/5 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10 backdrop-blur-sm">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center text-6xl font-black text-primary/10 group-hover:text-primary transition-all leading-none">01</div>
              <div className="flex-1">
                <h3 className="text-xl lg:text-2xl font-bold text-white tracking-tight mb-3 group-hover:text-primary transition-colors">No CTA. No Conversion.</h3>
                <p className="text-slate-400 leading-relaxed text-sm lg:text-base font-medium">
                  Most sales videos end without asking the buyer to do anything. No follow-up prompt. No urgency. The opportunity walks.
                </p>
              </div>
            </div>

            <div ref={nextReveal()} className="landing-reveal group relative flex flex-col items-start gap-6 p-8 bg-white/5 border border-white/10 rounded-[2rem] transition-all duration-500 shadow-sm shadow-primary/5 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10 backdrop-blur-sm">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center text-6xl font-black text-primary/10 group-hover:text-primary transition-all leading-none">02</div>
              <div className="flex-1">
                <h3 className="text-xl lg:text-2xl font-bold text-white tracking-tight mb-3 group-hover:text-primary transition-colors">Legal Exposure on Every Send</h3>
                <p className="text-slate-400 leading-relaxed text-sm lg:text-base font-medium">
                  A rep mentions a price. A financing guarantee. A competitor. It's on video &mdash; and now it's your liability. You won't know until it's too late.
                </p>
              </div>
            </div>

            <div ref={nextReveal()} className="landing-reveal group relative flex flex-col items-start gap-6 p-8 bg-white/5 border border-white/10 rounded-[2rem] transition-all duration-500 shadow-sm shadow-primary/5 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10 backdrop-blur-sm">
              <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center text-6xl font-black text-primary/10 group-hover:text-primary transition-all leading-none">03</div>
              <div className="flex-1">
                <h3 className="text-xl lg:text-2xl font-bold text-white tracking-tight mb-3 group-hover:text-primary transition-colors">Unmanageable at Scale</h3>
                <p className="text-slate-400 leading-relaxed text-sm lg:text-base font-medium">
                  You can't watch every video your team sends. But your brand and your compliance record live or die by what's in them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNALS */}
      <section className="py-24 lg:py-32 bg-white dark:bg-slate-950 text-slate-900 dark:text-white overflow-hidden relative" id="signals">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col gap-10 mb-20">
            <div ref={nextReveal()} className="landing-reveal transition-all duration-1000">
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-primary mb-6 block">The Intelligence Layer</span>
              <h2 className="font-sans text-4xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-slate-900 dark:text-white mb-0">
                Ten Signals.<br />One Score.<br />Total Clarity.
              </h2>
            </div>
            <div ref={nextReveal()} className="landing-reveal transition-all duration-1000 lg:pb-2">
              <p className="text-lg lg:text-xl text-slate-500 dark:text-slate-400 leading-relaxed font-medium max-w-xl">
                VQS Sales scores every video automatically across the ten dimensions that actually predict whether a buyer takes action &mdash; from visual quality to the energy in your rep's voice.
              </p>
            </div>
          </div>
          
          <div ref={nextReveal()} className="landing-reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mt-20">
            {SIGNALS.map((s) => (
              <div key={s.num} className="dark:bg-slate-900 border border-slate-100 dark:border-white/10 backdrop-blur-xl p-6 lg:p-8 rounded-[1.5rem] flex flex-col gap-4 group hover:bg-white dark:hover:bg-slate-800 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 relative overflow-hidden">
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
                <div className="text-4xl font-black text-primary/10 group-hover:text-primary transition-all leading-none relative z-10">
                  {s.num}
                </div>
                <div className="text-lg lg:text-xl font-bold text-slate-900 dark:text-white tracking-tight group-hover:text-primary transition-colors relative z-10">
                  {s.name}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed relative z-10">
                  {s.desc}
                </div>
                <span className={`relative z-10 inline-block text-[9px] font-black tracking-widest uppercase px-3 py-1 rounded-full w-fit mt-auto border ${
                  s.tag === 'ai' ? 'bg-purple-500/20 text-purple-400 border-purple-500/20' : 
                  s.tag === 'audio' ? 'bg-green-500/20 text-green-400 border-green-500/20' : 
                  'bg-primary/20 text-primary border-primary/20'
                }`}>
                  {s.tag === 'ai' ? 'AI-Powered' : s.tag === 'audio' ? 'Audio' : 'Visual'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="py-24 lg:py-32 bg-slate-900 text-white transition-colors relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div ref={nextReveal()} className="landing-reveal transition-all duration-1000">
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-primary mb-6 block">Compliance Engine</span>
              <h2 className="font-sans text-4xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-white mb-8">
                Stop Liability<br />Before It<br />Leaves the Lot.
              </h2>
              <div className="bg-white/5 border-l-4 border-primary p-8 lg:p-10 rounded-r-3xl mt-12 shadow-sm backdrop-blur-sm">
                <p className="font-sans italic text-lg lg:text-xl text-slate-300 leading-relaxed">
                  &ldquo;One verbal pricing commitment on video can cost more than a decade of software contracts. VQS Sales stops it before it sends.&rdquo;
                </p>
              </div>
            </div>
            
            <div ref={nextReveal()} className="landing-reveal transition-all duration-1000">
              <p className="text-lg text-slate-400 leading-relaxed mb-12">
                VQS Sales includes a pre-send compliance layer that automatically flags &mdash; and blocks &mdash; videos before they reach a customer. Every event is logged in a full compliance audit trail.
              </p>
              
              <div className="space-y-3">
                {COMPLIANCE_FLAGS.map((flag) => (
                  <div key={flag.name} className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-xl transition-all hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 group shadow-sm backdrop-blur-sm">
                    <div className={`w-10 h-10 shrink-0 flex items-center justify-center font-bold rounded-lg border ${
                      flag.severity === 'block' ? 'bg-red-500/20 text-red-500 border-red-500/30' : 'bg-orange-500/20 text-orange-500 border-orange-500/30'
                    }`}>
                      {flag.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-white leading-tight">{flag.name}</div>
                      <div className="text-[10px] font-medium text-slate-500 mt-0.5">{flag.method}</div>
                    </div>
                    <span className={`text-[9px] font-black tracking-widest uppercase px-2.5 py-1 rounded-md border ${
                      flag.severity === 'block' ? 'bg-red-500/20 text-red-500 border-red-500/40' : 'bg-orange-500/20 text-orange-500 border-orange-500/40'
                    }`}>
                      {flag.severity === 'block' ? 'Block' : 'Warn'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GRADES */}
      <section className="py-24 lg:py-32 px-6 bg-white dark:bg-slate-950 transition-colors relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div ref={nextReveal()} className="landing-reveal transition-all duration-1000">
            <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-primary mb-6 block">Scoring System</span>
            <h2 className="font-sans text-4xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-slate-900 dark:text-white mb-16">
              A Single Grade<br />Every Manager<br />Can Act On.
            </h2>
          </div>
          
          <div ref={nextReveal()} className="landing-reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {GRADES.map((g) => (
              <div 
                key={g.letter} 
                className={`p-10 rounded-[2.5rem] flex flex-col items-center text-center gap-2 border border-slate-100 dark:border-white/5 transition-all duration-500 hover:shadow-2xl hover:bg-slate-50 dark:hover:bg-slate-900 hover:border-primary/20 ${
                  g.cls === 'a' ? 'bg-green-500/5' :
                  g.cls === 'b' ? 'bg-green-500/5' :
                  g.cls === 'c' ? 'bg-orange-500/5' :
                  g.cls === 'd' ? 'bg-red-500/5' :
                  'bg-red-600/5'
                }`}
              >
                <div className={`text-8xl font-black leading-none mb-2 ${
                  g.cls === 'a' ? 'text-green-600' :
                  g.cls === 'b' ? 'text-green-500' :
                  g.cls === 'c' ? 'text-orange-500' :
                  g.cls === 'd' ? 'text-red-500' :
                  'text-red-700'
                }`}>{g.letter}</div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">{g.label}</div>
                <div className="text-xs font-bold text-slate-400 mb-2">{g.range}</div>
                <div className="text-xs text-slate-500 leading-relaxed font-medium">{g.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM */}
      <section className="py-24 lg:py-32 px-6 bg-slate-900 text-white transition-colors relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div ref={nextReveal()} className="landing-reveal transition-all duration-1000">
            <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-primary mb-6 block">Built on Real Scale</span>
            <h2 className="font-sans text-4xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-white mb-8">
              The Platform<br />No Competitor<br />Can Replicate.
            </h2>
            <p className="text-lg lg:text-xl text-slate-300 leading-relaxed max-w-2xl font-medium">
              VQS Sales is built on TruVideo's proprietary video intelligence infrastructure &mdash; the same platform that already powers inspection, communication, and AI workflows across the automotive industry.
            </p>
          </div>
          
          <div ref={nextReveal()} className="landing-reveal mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            <div className="bg-white/5 border border-white/10 backdrop-blur-md p-10 lg:p-12 rounded-[2.5rem] flex flex-col gap-6 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -right-4 -top-4 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
              <div className="text-6xl lg:text-8xl font-black text-primary transition-transform leading-none relative z-10">80M+</div>
              <div className="text-base text-slate-400 leading-relaxed font-medium relative z-10">
                Automotive videos processed &mdash; the largest domain-specific corpus in the industry
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 backdrop-blur-md p-10 lg:p-12 rounded-[2.5rem] flex flex-col gap-6 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -right-4 -top-4 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
              <div className="text-6xl lg:text-8xl font-black text-primary transition-transform leading-none relative z-10">10</div>
              <div className="text-base text-slate-400 leading-relaxed font-medium relative z-10">
                Precision scoring signals spanning visual quality, audio performance, and AI-powered analysis
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 backdrop-blur-md p-10 lg:p-12 rounded-[2.5rem] flex flex-col gap-6 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 group relative overflow-hidden">
              <div className="absolute -right-4 -top-4 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
              <div className="text-6xl lg:text-8xl font-black text-primary transition-transform leading-none relative z-10">9</div>
              <div className="text-base text-slate-400 leading-relaxed font-medium relative z-10">
                Compliance flags &mdash; from hard blocks to soft warnings &mdash; with full audit trail logging
              </div>
            </div>
          </div>
          
          <div ref={nextReveal()} className="landing-reveal mt-20">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-8 ml-2">Integrated across your stack</div>
            <div className="flex flex-wrap gap-4">
              {PARTNERS.map((p) => (
                <span key={p} className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-xs font-bold uppercase tracking-wider backdrop-blur-sm hover:border-primary/30 transition-all cursor-default text-slate-400">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-40 px-6 bg-primary dark:bg-slate-900 text-white text-center relative overflow-hidden" id="contact">
        {/* Background Accent */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 ref={nextReveal()} className="font-sans text-5xl lg:text-8xl font-extrabold leading-[1.05] tracking-tighter text-white mb-8 landing-reveal transition-all duration-1000">
            See VQS Sales<br />in Action.
          </h2>
          <p ref={nextReveal()} className="text-lg lg:text-2xl text-white/70 leading-relaxed mb-12 landing-reveal transition-all duration-1000 font-medium">
            Request a demo and we'll score a sample of your own team's videos in the session. See your real scores &mdash; and your real compliance risk &mdash; live.
          </p>
          <div ref={nextReveal()} className="flex flex-wrap justify-center gap-6 landing-reveal transition-all duration-1000">
            <a 
              href="mailto:sales@truvideo.com" 
              className="h-14 px-10 rounded-2xl bg-white text-primary font-bold shadow-2xl shadow-black/10 hover:scale-[1.02] transition-all flex items-center justify-center text-base active:scale-[0.98]"
            >
              Request a Demo
            </a>
            <a 
              href="mailto:press@truvideo.com" 
              className="h-14 px-10 rounded-2xl border-2 border-white/20 text-white font-bold hover:bg-white/5 transition-all flex items-center justify-center text-base active:scale-[0.98]"
            >
              Media Inquiries
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <img src={logoSrc} alt="TruVideo" className="h-8" />
          <div className="flex flex-col md:flex-row items-center gap-6 text-sm font-semibold text-slate-400 dark:text-slate-600">
            <p>&copy; 2026 TruVideo. All rights reserved.</p>
            <p className="hidden md:block text-slate-200 dark:text-white/10">|</p>
            <a href="mailto:press@truvideo.com" className="hover:text-primary transition-colors font-mono uppercase tracking-widest text-[10px]">press@truvideo.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
